<template>
    <Dialog
        v-model:visible="visible"
        :style="{ width: '800px' }"
        :header="modalTitle"
        :modal="true"
        :closable="!form.processing"
        @hide="onHide"
    >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Basic Information -->
            <div class="col-span-2">
                <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('Basic Information') }}</h4>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Trade Name') }} <span class="text-red-500">*</span></label>
                <InputText
                    v-model="form.name"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.name }"
                    :placeholder="$t('Enter trade name')"
                    :disabled="form.processing"
                />
                <small class="p-error" v-if="form.errors.name">{{ form.errors.name }}</small>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Legal Name') }} <span class="text-red-500">*</span></label>
                <InputText
                    v-model="form.legal_name"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.legal_name }"
                    :placeholder="$t('Enter legal name')"
                    :disabled="form.processing"
                />
                <small class="p-error" v-if="form.errors.legal_name">{{ form.errors.legal_name }}</small>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Document Type') }}</label>
                <Dropdown
                    v-model="form.document_type"
                    :options="documentTypes"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Document Number') }} <span class="text-red-500">*</span></label>
                <InputText
                    v-model="form.document_number"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.document_number }"
                    :placeholder="$t('Enter document number')"
                    :disabled="form.processing"
                />
                <small class="p-error" v-if="form.errors.document_number">{{ form.errors.document_number }}</small>
            </div>

            <!-- Address -->
            <div class="col-span-2 mt-2">
                <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('Address') }}</h4>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Street') }}</label>
                <InputText
                    v-model="form.address_street"
                    class="w-full"
                    :placeholder="$t('Enter street')"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Number') }}</label>
                <InputText
                    v-model="form.address_number"
                    class="w-full"
                    :placeholder="$t('Enter number')"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('City') }}</label>
                <InputText
                    v-model="form.address_city"
                    class="w-full"
                    :placeholder="$t('Enter city')"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('State') }}</label>
                <InputText
                    v-model="form.address_state"
                    class="w-full"
                    :placeholder="$t('Enter state')"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Country') }}</label>
                <InputText
                    v-model="form.address_country"
                    class="w-full"
                    :placeholder="$t('Enter country')"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Zip Code') }}</label>
                <InputText
                    v-model="form.address_zipcode"
                    class="w-full"
                    :placeholder="$t('Enter zip code')"
                    :disabled="form.processing"
                />
            </div>

            <!-- Contact -->
            <div class="col-span-2 mt-2">
                <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('Contact') }}</h4>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Email') }}</label>
                <InputText
                    v-model="form.email"
                    type="email"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.email }"
                    :placeholder="$t('Enter email')"
                    :disabled="form.processing"
                />
                <small class="p-error" v-if="form.errors.email">{{ form.errors.email }}</small>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Phone') }}</label>
                <InputText
                    v-model="form.phone"
                    class="w-full"
                    :placeholder="$t('Enter phone')"
                    :disabled="form.processing"
                />
            </div>

            <!-- Tax Information -->
            <div class="col-span-2 mt-2">
                <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('Tax Information') }}</h4>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('VAT Number') }}</label>
                <InputText
                    v-model="form.vat_number"
                    class="w-full"
                    :placeholder="$t('Enter VAT number')"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Municipal Registration') }}</label>
                <InputText
                    v-model="form.municipal_registration"
                    class="w-full"
                    :placeholder="$t('Enter municipal registration')"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('State Registration') }}</label>
                <InputText
                    v-model="form.state_registration"
                    class="w-full"
                    :placeholder="$t('Enter state registration')"
                    :disabled="form.processing"
                />
            </div>
        </div>

        <template #footer>
            <Button
                :label="$t('Cancel')"
                icon="pi pi-times"
                text
                @click="close"
                :disabled="form.processing"
            />
            <Button
                :label="isEditing ? $t('Update Company') : $t('Save Company')"
                icon="pi pi-save"
                severity="primary"
                @click="save"
                :loading="form.processing"
                :disabled="form.processing"
            />
        </template>
    </Dialog>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { useForm } from '@inertiajs/vue3';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    company: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['update:modelValue', 'saved']);

const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

const isEditing = computed(() => props.company && props.company.id);

const modalTitle = computed(() => {
    return isEditing.value ? $t('Edit Company') : $t('New Company');
});

const documentTypes = [
    { label: 'EIN', value: 'EIN' },
    { label: 'CNPJ', value: 'CNPJ' },
    { label: 'CPF', value: 'CPF' },
];

const form = useForm({
    name: '',
    legal_name: '',
    document_type: 'CNPJ',
    document_number: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_country: '',
    address_zipcode: '',
    phone: '',
    email: '',
    vat_number: '',
    municipal_registration: '',
    state_registration: '',
    is_active: true,
    sort_order: 0,
});

// Watch for company changes to populate form
watch(() => props.company, (newCompany) => {
    if (newCompany) {
        Object.keys(form.data()).forEach(key => {
            if (newCompany[key] !== undefined) {
                form[key] = newCompany[key];
            }
        });
    } else {
        form.reset();
    }
}, { immediate: true });

// Watch for modal visibility to reset form when opening for new
watch(() => props.modelValue, (isVisible) => {
    if (isVisible && !props.company) {
        form.reset();
    }
});

const close = () => {
    visible.value = false;
};

const onHide = () => {
    form.reset();
    form.clearErrors();
};

const save = () => {

    const requiredFields = {
        name: $t('Trade name is required'),
        legal_name: $t('Legal name is required'),
        document_number: $t('Document number is required'),
    };

    form.clearErrors();
    let hasErrors = false;

    Object.entries(requiredFields).forEach(([field, message]) => {
        if (!form[field] || !form[field].trim()) {
            form.setError(field, $t(message));
            hasErrors = true;
        }
    });

    if (hasErrors) {
        toast.add({
            severity: 'warn',
            summary: $t('Validation Error'),
            detail: $t('Please fill in all required fields'),
            life: 3000,
        });
        return;
    }

    const url = isEditing.value
        ? route('companies.update', props.company.id)
        : route('companies.store');
    const method = isEditing.value ? 'put' : 'post';

    form[method](url, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: isEditing.value ? $t('Company updated successfully') : $t('Company created successfully'),
                life: 3000
            });
            visible.value = false;
            emit('saved');
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to save company'),
                life: 5000
            });
        }
    });
};
</script>

<style scoped>
.p-error {
    color: var(--red-500);
    font-size: 0.75rem;
    margin-top: 0.25rem;
    display: block;
}
</style>
